import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, ArrowLeft, Users, UserPlus, Sparkles, Filter, Check, 
  MessageSquare, Phone, Clock, ArrowRight, LogOut, ShieldCheck, Globe,
  BookOpen, Target, Calendar, ChevronRight, X, ExternalLink
} from 'lucide-react';
import API_URL from '../../config';
import EventProfileOnboardingModal from '../../components/events/EventProfileOnboardingModal';
import ExternalProfileOnboardingModal from '../../components/events/ExternalProfileOnboardingModal';
import EventParticipantProfileModal from '../../components/events/EventParticipantProfileModal';
import './Events.css';
import './EventsPrototype.css';

export default function EventNetworkingHome() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isExternal, setIsExternal] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [recommendations, setRecommendations] = useState([]);
  const [connections, setConnections] = useState([]);
  const [pendingReceived, setPendingReceived] = useState([]);
  const [pendingSent, setPendingSent] = useState([]);
  const [selectedProfileIndex, setSelectedProfileIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all'); // all, external, internal
  const [toastMessage, setToastMessage] = useState(null);
  const [completion, setCompletion] = useState(70);
  // New states for prototype UX
  const [showFilters, setShowFilters] = useState(false);
  const [filterSkill, setFilterSkill] = useState('');
  const [filterIntent, setFilterIntent] = useState('all');
  const [swipeIndex, setSwipeIndex] = useState(0);
  const [passedEmails, setPassedEmails] = useState(new Set());
  const [connectingEmail, setConnectingEmail] = useState(null);
  const swipeCardRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });

  // Strict Light Theme enforcement for all event pages
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  // Auth & Session Check
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('vjconnect_user'));
    const storedExternal = JSON.parse(localStorage.getItem('convergence_external_user'));
    const storedExternalToken = localStorage.getItem('convergence_external_token');

    if (storedUser && storedUser.email) {
      setUser(storedUser);
      setIsExternal(false);
    } else if (storedExternal && storedExternalToken) {
      setUser(storedExternal);
      setIsExternal(true);
    } else {
      navigate(`/events/${slug}`, { replace: true });
    }
  }, [navigate, slug]);

  const handleLogout = () => {
    if (isExternal) {
      const token = localStorage.getItem('convergence_external_token');
      if (token) {
        fetch(`${API_URL}/events-external-auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => {});
      }
      localStorage.removeItem('convergence_external_token');
      localStorage.removeItem('convergence_external_user');
      navigate(`/events/${slug}`);
    } else {
      localStorage.removeItem('vjconnect_user');
      navigate('/');
    }
  };

  const refreshProfileAndData = (userObj) => {
    const currentUser = userObj || user;
    if (!currentUser || !slug) return;
    const activeEmail = currentUser.email || currentUser.login_email;
    if (!activeEmail) return;

    fetch(`${API_URL}/events-networking/${slug}/profile/me?email=${activeEmail}`)
      .then(res => res.json())
      .then(data => {
        if (data && data.completion !== undefined) setCompletion(data.completion);
        if (data && data.user) {
          setUser(prev => ({ ...prev, ...data.user }));
        }
        const locallyCompleted = localStorage.getItem(`vjlink_profile_completed_${slug}_${activeEmail}`) === 'true';
        if (data && (data.exists || data.profile || data.profile_completed || locallyCompleted)) {
          setShowOnboarding(false);
          loadRecommendations(currentUser);
        } else {
          setShowOnboarding(true);
        }
      }).catch(() => {
        setShowOnboarding(false);
        loadRecommendations(currentUser);
      });
  };

  useEffect(() => {
    if (user && slug) {
      fetch(`${API_URL}/events/${slug}`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.event) {
            setEventData(data.event);
          } else {
            setEventData({ name: slug.toUpperCase(), slug });
          }
        })
        .catch(() => setEventData({ name: slug.toUpperCase(), slug }));

      refreshProfileAndData(user);
    }
  }, [slug, user?.email]);

  const isExternalParticipant = (p) => Boolean(
    p && (
      p.is_external === true ||
      p.is_external === 1 ||
      p.is_external === '1' ||
      p.is_external === 'true' ||
      (p.college && p.college.toLowerCase() !== 'vnr vjiet') ||
      (p.email && !p.email.endsWith('@vnrvjiet.in'))
    )
  );

  const loadRecommendations = (currentUser) => {
    const u = currentUser || user;
    if (!u || !slug) return;
    const activeEmail = (u.email || u.login_email || '').toLowerCase().trim();
    if (!activeEmail) return;

    Promise.all([
      fetch(`${API_URL}/events-networking/${slug}/recommendations?email=${activeEmail}&refresh=true`)
        .then(res => res.json())
        .catch(() => ({ recommendations: [] })),
      fetch(`${API_URL}/events-networking/${slug}/connections?email=${activeEmail}`)
        .then(res => res.json())
        .catch(() => ({ accepted: [], pending_sent: [], pending_received: [] }))
    ]).then(([recData, connData]) => {
      const accepted = connData.accepted || [];
      const sent = connData.pending_sent || [];
      const received = connData.pending_received || [];

      setConnections(accepted);
      setPendingSent(sent);
      setPendingReceived(received);

      const acceptedEmails = new Set(accepted.map(c => (c.email || '').toLowerCase().trim()));
      const sentEmails = new Set(sent.map(c => (c.email || '').toLowerCase().trim()));
      const receivedEmails = new Set(received.map(c => (c.email || '').toLowerCase().trim()));

      let recList = recData.recommendations || [];
      if (!recList.length && recData.categories) {
        recList = Object.values(recData.categories).flat();
      }

      const mergedRecs = recList.map(r => {
        const rEmail = (r.email || '').toLowerCase().trim();
        let status = 'none';
        if (acceptedEmails.has(rEmail)) {
          status = 'connected';
        } else if (sentEmails.has(rEmail)) {
          status = 'pending_sent';
        } else if (receivedEmails.has(rEmail)) {
          status = 'pending_received';
        }
        const isConn = status === 'connected';
        let safeCategory = r.category;
        if (!isConn && safeCategory === 'connection') {
          safeCategory = 'similar';
        } else if (isConn) {
          safeCategory = 'connection';
        }
        return { 
          ...r, 
          connectionStatus: status,
          isConnected: isConn,
          category: safeCategory
        };
      });

      setRecommendations(mergedRecs);
    }).catch(err => {
      console.error('Error loading recommendations & connections:', err);
    });
  };

  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  }, []);

  const handleConnect = async (participant) => {
    if (!participant || !participant.email) return;
    const targetEmail = participant.email.toLowerCase().trim();
    if (connectingEmail === targetEmail) return;
    setConnectingEmail(targetEmail);
    try {
      await fetch(`${API_URL}/events-networking/${slug}/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          requester_email: user.email, 
          receiver_email: participant.email, 
          requester_name: user.name 
        })
      });
      setRecommendations(prev => prev.map(p => (p.email || '').toLowerCase().trim() === targetEmail ? { ...p, connectionStatus: 'pending_sent' } : p));
      setPendingSent(prev => {
        if (prev.some(p => (p.email || '').toLowerCase().trim() === targetEmail)) return prev;
        return [...prev, { ...participant, connectionStatus: 'pending_sent' }];
      });
      showToast(`Connection request sent to ${participant.name.split(' ')[0]}`);
    } catch (e) {
      console.error(e);
      showToast('Failed to send request. Try again.');
    } finally {
      setConnectingEmail(null);
    }
  };

  const handleAcceptConnect = async (participant) => {
    if (!participant || !participant.email) return;
    const targetEmail = participant.email.toLowerCase().trim();
    try {
      await fetch(`${API_URL}/events-networking/${slug}/connect/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          requester_email: participant.email, 
          receiver_email: user.email, 
          accepter_name: user.name 
        })
      });
      setPendingReceived(prev => prev.filter(p => (p.email || '').toLowerCase().trim() !== targetEmail));
      setConnections(prev => {
        if (prev.some(p => (p.email || '').toLowerCase().trim() === targetEmail)) return prev;
        return [...prev, { ...participant, connectionStatus: 'connected' }];
      });
      setRecommendations(prev => prev.map(p => (p.email || '').toLowerCase().trim() === targetEmail ? { ...p, connectionStatus: 'connected', category: 'connection' } : p));
      showToast(`Connected with ${participant.name.split(' ')[0]}! Contact details unlocked.`);
      loadRecommendations(user);
    } catch (e) {
      console.error(e);
    }
  };

  const handleRejectConnect = async (participant) => {
    if (!participant || !participant.email) return;
    const targetEmail = participant.email.toLowerCase().trim();
    try {
      await fetch(`${API_URL}/events-networking/${slug}/connect/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requester_email: participant.email, receiver_email: user.email })
      });
      setPendingReceived(prev => prev.filter(p => (p.email || '').toLowerCase().trim() !== targetEmail));
      setRecommendations(prev => prev.map(p => (p.email || '').toLowerCase().trim() === targetEmail ? { ...p, connectionStatus: 'none' } : p));
    } catch (e) {
      console.error(e);
    }
  };

  const handleRemoveConnect = async (participant) => {
    if (!participant || !participant.email) return;
    const targetEmail = (participant.email || '').toLowerCase().trim();
    const myEmail = (user?.email || user?.login_email || '').toLowerCase().trim();
    if (!myEmail) return;

    if (!window.confirm(`Disconnect from ${participant.name || 'this participant'}?`)) return;

    // Immediately remove from local UI state and close drawer
    setConnections(prev => prev.filter(c => (c.email || '').toLowerCase().trim() !== targetEmail));
    setRecommendations(prev => prev.map(p => {
      if ((p.email || '').toLowerCase().trim() === targetEmail) {
        return { 
          ...p, 
          connectionStatus: 'none', 
          isConnected: false, 
          category: p.category === 'connection' ? 'similar' : p.category 
        };
      }
      return p;
    }));
    closeProfile();
    showToast(`Disconnected from ${participant.name || 'participant'}`);

    try {
      const res = await fetch(`${API_URL}/events-networking/${slug}/connect/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: myEmail,
          target_email: targetEmail
        })
      });
      const data = await res.json();
      if (!data.success) {
        showToast(data.error || 'Failed to disconnect');
      }
      // Re-sync atomically with database
      loadRecommendations(user);
    } catch (e) {
      console.error('Error removing connection:', e);
      showToast('Error removing connection. Try again.');
      loadRecommendations(user);
    }
  };

  // ===== FILTERING LOGIC (PRESERVED FROM ORIGINAL) =====
  const filteredRecs = useMemo(() => {
    return recommendations.filter(rec => {
      if (selectedFilter === 'external' && !isExternalParticipant(rec)) return false;
      if (selectedFilter === 'internal' && isExternalParticipant(rec)) return false;
      if (filterIntent !== 'all') {
        const intentMatch = (rec.networking_intent || rec.category || '').toLowerCase().includes(filterIntent);
        if (!intentMatch) return false;
      }
      if (filterSkill.trim()) {
        const q = filterSkill.toLowerCase();
        const skillsText = typeof rec.skills === 'string' ? rec.skills : JSON.stringify(rec.skills || []);
        const interestsText = typeof rec.interestsTechnical === 'string' ? rec.interestsTechnical : JSON.stringify(rec.interestsTechnical || []);
        if (![skillsText, interestsText].join(' ').toLowerCase().includes(q)) return false;
      }
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const skillsText = typeof rec.skills === 'string' ? rec.skills : JSON.stringify(rec.skills || []);
      const interestsText = typeof rec.interestsTechnical === 'string' ? rec.interestsTechnical : JSON.stringify(rec.interestsTechnical || []);
      return (
        (rec.name && rec.name.toLowerCase().includes(q)) ||
        (rec.department && rec.department.toLowerCase().includes(q)) ||
        (rec.college && rec.college.toLowerCase().includes(q)) ||
        (rec.school_name && rec.school_name.toLowerCase().includes(q)) ||
        (rec.junior_college && rec.junior_college.toLowerCase().includes(q)) ||
        skillsText.toLowerCase().includes(q) ||
        interestsText.toLowerCase().includes(q)
      );
    });
  }, [recommendations, searchQuery, selectedFilter, filterSkill, filterIntent]);

  // Category groupings
  const categoryGroups = useMemo(() => {
    return filteredRecs.reduce((acc, rec) => {
      const cat = rec.category || 'similar';
      acc[cat] = acc[cat] || [];
      acc[cat].push(rec);
      return acc;
    }, {});
  }, [filteredRecs]);

  const categoryLabels = {
    'collaborator': 'Potential Collaborators',
    'teammate': 'Potential Teammates',
    'similar': 'Similar Technical Interests',
    'alumni_school': 'School Alumni',
    'alumni_college': 'Junior College Alumni',
    'activity_match': 'In Your Activities',
    'connection': 'Connected on VJLINK'
  };

  const connectedEmailSet = useMemo(() => {
    return new Set(connections.map(c => (c.email || '').toLowerCase().trim()));
  }, [connections]);

  const pendingSentEmailSet = useMemo(() => {
    return new Set(pendingSent.map(p => (p.email || '').toLowerCase().trim()));
  }, [pendingSent]);

  // ===== SWIPE CARD QUEUE =====
  const swipeQueue = useMemo(() => {
    const myEmail = (user?.email || user?.login_email || '').toLowerCase().trim();
    let q = filteredRecs.filter(r => {
      const rEmail = (r.email || '').toLowerCase().trim();
      if (!rEmail || rEmail === myEmail) return false;
      if (r.connectionStatus === 'connected' || r.category === 'connection') return false;
      if (r.connectionStatus === 'pending_sent') return false;
      if (connectedEmailSet.has(rEmail)) return false;
      if (pendingSentEmailSet.has(rEmail)) return false;
      if (passedEmails.has(rEmail)) return false;
      return true;
    });

    // Apply mode-based filtering
    if (activeCategory === 'collaborator') {
      q = q.filter(r => (r.category === 'collaborator' || r.category === 'similar'));
    } else if (activeCategory === 'teammate') {
      q = q.filter(r => r.category === 'teammate');
    }
    return q;
  }, [filteredRecs, passedEmails, activeCategory, user, connectedEmailSet, pendingSentEmailSet]);

  const safeSwipeIndex = Math.min(swipeIndex, Math.max(0, swipeQueue.length - 1));
  const currentCard = swipeQueue[safeSwipeIndex] || null;

  // Connections for "My Network" view
  const networkSearched = useMemo(() => {
    if (!searchQuery.trim()) return connections;
    const q = searchQuery.toLowerCase();
    return connections.filter(p =>
      [p.name, p.college, p.department, ...(Array.isArray(p.skills) ? p.skills : [])].join(' ').toLowerCase().includes(q)
    );
  }, [connections, searchQuery]);

  // ===== SWIPE GESTURES =====
  const handleTouchStart = (e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    const card = swipeCardRef.current;
    if (card) card.classList.add('swiping');
  };

  const handleTouchMove = (e) => {
    const card = swipeCardRef.current;
    if (!card) return;
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = e.touches[0].clientY - touchStartRef.current.y;
    if (Math.abs(dx) > Math.abs(dy)) {
      card.style.transform = `translateX(${dx * 0.65}px) rotate(${dx * 0.025}deg)`;
      card.style.opacity = String(Math.max(0.55, 1 - Math.abs(dx) / 420));
    }
  };

  const handleTouchEnd = (e) => {
    const card = swipeCardRef.current;
    if (!card) return;
    card.classList.remove('swiping');
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    if (dx > 90 && currentCard) {
      handleConnectCurrent();
    } else if (dx < -90 && currentCard) {
      handleSkipCurrent();
    } else {
      card.style.transform = '';
      card.style.opacity = '1';
    }
  };

  const handleSkipCurrent = () => {
    if (!currentCard) return;
    const emailToPass = (currentCard.email || '').toLowerCase().trim();
    setPassedEmails(prev => new Set([...prev, emailToPass]));
    showToast('Skipped for now');
  };

  const handleConnectCurrent = () => {
    if (!currentCard) return;
    handleConnect(currentCard);
  };

  // Active drawer profile
  const currentDisplayedList = useMemo(() => {
    if (activeCategory === 'connections') return connections;
    if (activeCategory === 'requests') return pendingReceived;
    if (activeCategory === 'all' || activeCategory === 'recommended') return filteredRecs;
    return categoryGroups[activeCategory] || [];
  }, [activeCategory, connections, pendingReceived, filteredRecs, categoryGroups]);

  const openCurrentProfile = () => {
    if (!currentCard) return;
    const targetEmail = (currentCard.email || '').toLowerCase().trim();
    const idx = currentDisplayedList.findIndex(p => (p.email || '').toLowerCase().trim() === targetEmail);
    setSelectedProfileIndex(idx >= 0 ? idx : 0);
  };

  const activeProfile = selectedProfileIndex >= 0 && currentDisplayedList[selectedProfileIndex] ? currentDisplayedList[selectedProfileIndex] : null;

  const navigateDrawer = (delta) => {
    const newIdx = selectedProfileIndex + delta;
    if (newIdx >= 0 && newIdx < currentDisplayedList.length) {
      setSelectedProfileIndex(newIdx);
    }
  };

  const openProfile = (participant) => {
    if (!participant) return;
    const targetEmail = (participant.email || '').toLowerCase().trim();
    const idx = currentDisplayedList.findIndex(p => (p.email || '').toLowerCase().trim() === targetEmail);
    setSelectedProfileIndex(idx >= 0 ? idx : 0);
  };

  const closeProfile = () => {
    setSelectedProfileIndex(-1);
  };

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedFilter('all');
    setFilterSkill('');
    setFilterIntent('all');
    setShowFilters(false);
    setSwipeIndex(0);
  };

  // Helper to render a person's initials
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ').filter(Boolean);
    return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name[0].toUpperCase();
  };

  // Helper to pick cover color based on name
  const getCoverColor = (name) => {
    if (!name) return 'blue';
    const colors = ['blue', 'peach', 'lilac', 'green'];
    const idx = name.charCodeAt(0) % colors.length;
    return colors[idx];
  };

  // Helper: safe array
  const safeArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try { const parsed = JSON.parse(val); return Array.isArray(parsed) ? parsed : [String(parsed)]; }
    catch { return String(val).split(',').map(s => s.trim()).filter(Boolean); }
  };

  // ===== RENDER =====
  const isDiscoverView = activeCategory === 'all' || activeCategory === 'recommended' || activeCategory === 'collaborator' || activeCategory === 'teammate';
  const isRequestsView = activeCategory === 'requests';
  const isNetworkView = activeCategory === 'connections';

  return (
    <div className="events-page vp-font" data-theme="light">
      {/* ====== MAIN CONTENT ====== */}
      <div className="events-wrapper">
        {/* ====== DISCOVER VIEW ====== */}
        {isDiscoverView && (
          <div className="vp-view">
            <div className="vp-discover-head">
              <div>
                <div className="vp-eyebrow">{eventData?.name || 'Event'} · Networking Space</div>
                <h1>Find your kind of people.</h1>
                <p className="vp-lede">
                  Less endless scrolling. More meaningful introductions. Explore one profile at a time and connect when it feels right.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                {user && (
                  <button className="vp-btn vp-btn-outline" onClick={() => setShowOnboarding(true)}>
                    Edit my profile ↗
                  </button>
                )}
                <button 
                  className="vp-btn vp-btn-soft vp-btn-small" 
                  onClick={handleLogout}
                  style={{ color: '#a53b31' }}
                  title="Exit networking"
                >
                  <LogOut size={14} /> Exit
                </button>
              </div>
            </div>

            <div className="vp-discover-layout">
              <div className="vp-discover-main">
                {/* Search & Filter Toolbar */}
                <div className="vp-discover-toolbar">
                  <label className="vp-search">
                    <span aria-hidden="true"><Search size={16} style={{ color: 'var(--vp-muted)' }} /></span>
                    <input 
                      placeholder="Search skills, interests, people…" 
                      value={searchQuery}
                      onChange={e => { setSearchQuery(e.target.value); setSwipeIndex(0); }}
                    />
                  </label>
                  <button className="vp-filter-btn" onClick={() => setShowFilters(!showFilters)}>
                    Filters {(selectedFilter !== 'all' || filterIntent !== 'all' || filterSkill) ? '•' : ''} ☷
                  </button>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                  <div className="vp-filters-panel">
                    <div className="vp-form-grid">
                      <div className="vp-field">
                        <label>Participant type</label>
                        <select value={selectedFilter} onChange={e => { setSelectedFilter(e.target.value); setSwipeIndex(0); }}>
                          <option value="all">Everyone</option>
                          <option value="internal">VNR VJIET</option>
                          <option value="external">External attendees</option>
                        </select>
                      </div>
                      <div className="vp-field">
                        <label>Looking for</label>
                        <select value={filterIntent} onChange={e => { setFilterIntent(e.target.value); setSwipeIndex(0); }}>
                          <option value="all">Any goal</option>
                          <option value="teammate">Hackathon teammates</option>
                          <option value="collaborator">Project collaborators</option>
                          <option value="learning">Learning & mentorship</option>
                        </select>
                      </div>
                      <div className="vp-field full">
                        <label>Skill or interest</label>
                        <input 
                          placeholder="e.g. React, AI, design" 
                          value={filterSkill}
                          onChange={e => { setFilterSkill(e.target.value); setSwipeIndex(0); }}
                        />
                      </div>
                    </div>
                    <button className="vp-btn vp-btn-soft vp-btn-small" style={{ marginTop: 12 }} onClick={resetFilters}>
                      Reset filters
                    </button>
                  </div>
                )}

                {/* Mode Tabs */}
                <div className="vp-mode-tabs" role="tablist">
                  <button 
                    className={`vp-mode-tab ${(activeCategory === 'all' || activeCategory === 'recommended') ? 'active' : ''}`}
                    onClick={() => { setActiveCategory('all'); setSwipeIndex(0); }}
                  >For you</button>
                  <button 
                    className={`vp-mode-tab ${activeCategory === 'collaborator' ? 'active' : ''}`}
                    onClick={() => { setActiveCategory('collaborator'); setSwipeIndex(0); }}
                  >Potential collaborators</button>
                  <button 
                    className={`vp-mode-tab ${activeCategory === 'teammate' ? 'active' : ''}`}
                    onClick={() => { setActiveCategory('teammate'); setSwipeIndex(0); }}
                  >Team matches</button>
                </div>

                {/* ====== SWIPE CARD ====== */}
                {currentCard ? (
                  <div id="swipe-container">
                    <div className="vp-queue-progress">
                      <small>{String(swipeQueue.indexOf(currentCard) + 1).padStart(2, '0')} / {String(swipeQueue.length).padStart(2, '0')}</small>
                      <div className="vp-progress-line">
                        <span style={{ width: `${((swipeQueue.indexOf(currentCard) + 1) / swipeQueue.length) * 100}%` }} />
                      </div>
                      <small>{currentCard.score ? `${currentCard.score}% fit` : 'Curated'}</small>
                    </div>

                    <div className="vp-swipe-stage">
                      <article 
                        className="vp-swipe-card" 
                        ref={swipeCardRef}
                        tabIndex={0}
                        aria-label={`Profile of ${currentCard.name}`}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onKeyDown={e => {
                          if (e.key === 'ArrowRight') handleConnectCurrent();
                          if (e.key === 'ArrowLeft') handleSkipCurrent();
                          if (e.key === 'Enter') openCurrentProfile();
                        }}
                      >
                        <div className={`vp-person-cover ${getCoverColor(currentCard.name)}`}>
                          <div className="vp-person-avatar">
                            {currentCard.profile_pic 
                              ? <img src={currentCard.profile_pic} alt="" /> 
                              : getInitials(currentCard.name)
                            }
                          </div>
                          {currentCard.score && (
                            <div className="vp-match-label">{currentCard.score}% match</div>
                          )}
                        </div>
                        <div className="vp-person-content">
                          <div className="vp-person-name-line">
                            <div>
                              <h2>{currentCard.name}</h2>
                              <div className="vp-person-meta">
                                {isExternalParticipant(currentCard) 
                                  ? `${currentCard.college || 'External'} · ${currentCard.department || ''}`
                                  : `${currentCard.department || ''} · ${currentCard.year || ''}`
                                }
                              </div>
                            </div>
                            <span className="vp-origin-label">
                              {isExternalParticipant(currentCard) ? 'Event guest' : 'VJLINK member'}
                            </span>
                          </div>
                          
                          {currentCard.bio && <p className="vp-person-bio">{currentCard.bio}</p>}
                          
                          {/* Skills */}
                          {safeArray(currentCard.skills).length > 0 && (
                            <>
                              <div className="vp-person-section-label">Brings to the table</div>
                              <div className="vp-tag-row">
                                {safeArray(currentCard.skills).slice(0, 5).map((s, i) => (
                                  <span key={i} className="vp-skill-tag">{s}</span>
                                ))}
                              </div>
                            </>
                          )}

                          {/* Interests */}
                          {safeArray(currentCard.interestsTechnical || currentCard.technical_interests).length > 0 && (
                            <>
                              <div className="vp-person-section-label">Interested in</div>
                              <div className="vp-tag-row">
                                {safeArray(currentCard.interestsTechnical || currentCard.technical_interests).slice(0, 4).map((s, i) => (
                                  <span key={i} className="vp-skill-tag">{s}</span>
                                ))}
                              </div>
                            </>
                          )}

                          {/* Match insight */}
                          {currentCard.matchData?.reasons?.[0] && (
                            <div className="vp-match-insight">
                              <strong>Why this could click</strong>
                              {currentCard.matchData.reasons[0]}
                            </div>
                          )}
                        </div>
                      </article>
                    </div>

                    {/* Action Buttons */}
                    <div className="vp-swipe-actions">
                      <button className="vp-round-action pass" onClick={handleSkipCurrent} aria-label="Skip" title="Not now">✕</button>
                      <button className="vp-round-action" onClick={openCurrentProfile} aria-label="View profile" title="View profile">↗</button>
                      <button 
                        className="vp-round-action connect" 
                        onClick={handleConnectCurrent} 
                        aria-label="Connect" 
                        title="Connect"
                        disabled={connectingEmail === currentCard.email}
                      >＋</button>
                    </div>
                    <p className="vp-swipe-hint">Swipe left to pass · Swipe right to connect · Or use the buttons</p>
                  </div>
                ) : (
                  <div className="vp-empty-state">
                    <div className="vp-empty-icon">⌕</div>
                    <h3 className="vp-heading">
                      {filteredRecs.length === 0 ? 'No matches in this view' : 'All caught up!'}
                    </h3>
                    <p>
                      {filteredRecs.length === 0 
                        ? 'Try a different skill, goal or participant filter. Your next good connection may be one filter away.'
                        : 'You\'ve seen all recommendations. Check back later or clear passed profiles.'
                      }
                    </p>
                    {passedEmails.size > 0 && (
                      <button className="vp-btn vp-btn-dark" onClick={() => { setPassedEmails(new Set()); setSwipeIndex(0); }}>
                        Reset passed profiles
                      </button>
                    )}
                    {filteredRecs.length === 0 && (
                      <button className="vp-btn vp-btn-dark" onClick={resetFilters}>Clear filters</button>
                    )}
                  </div>
                )}
              </div>

              {/* ====== SIDE PANEL ====== */}
              <aside className="vp-side-panel">
                <div className="vp-eyebrow">Your networking profile</div>
                <h3 className="vp-heading" style={{ fontSize: 17, marginTop: 8 }}>Give people a reason to connect.</h3>
                <p style={{ fontSize: 12, color: 'var(--vp-muted)', margin: '4px 0 12px' }}>A little more context helps us show better-fit people.</p>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, fontWeight: 800 }}>
                  <span>Profile strength</span>
                  <span>{completion}%</span>
                </div>
                <div className="vp-profile-progress">
                  <span style={{ width: `${completion}%` }} />
                </div>
                <button className="vp-btn vp-btn-outline vp-btn-small" style={{ width: '100%' }} onClick={() => setShowOnboarding(true)}>
                  Improve profile
                </button>

                <div className="vp-rule" />
                <div className="vp-eyebrow">Your network, at a glance</div>
                
                <div className="vp-side-row">
                  <div className="vp-side-icon">↗</div>
                  <div>
                    <strong>{connections.length} connections</strong>
                    <span>People who accepted your request</span>
                  </div>
                </div>
                <div className="vp-side-row">
                  <div className="vp-side-icon">◎</div>
                  <div>
                    <strong>{pendingReceived.length} pending requests</strong>
                    <span>Requests waiting for a response</span>
                  </div>
                </div>
                <div className="vp-side-row">
                  <div className="vp-side-icon">✳</div>
                  <div>
                    <strong>Why this person?</strong>
                    <span>Every match has a reason, not just a score.</span>
                  </div>
                </div>

                <button className="vp-btn vp-btn-dark" style={{ width: '100%', marginTop: 14, justifyContent: 'center' }} onClick={() => setActiveCategory('connections')}>
                  Open my network ↗
                </button>
              </aside>
            </div>
          </div>
        )}

        {/* ====== REQUESTS VIEW ====== */}
        {isRequestsView && (
          <div className="vp-view">
            <div className="vp-section-intro">
              <div>
                <div className="vp-eyebrow">Good things start with an invite</div>
                <h1 className="vp-heading" style={{ fontSize: 'clamp(32px,4.5vw,48px)' }}>Connection requests.</h1>
                <p>Review incoming invitations, see why someone reached out, and decide who you want in your network.</p>
              </div>
              <button className="vp-btn vp-btn-outline" onClick={() => setActiveCategory('all')}>Discover people ↗</button>
            </div>

            {/* Received */}
            <div className="vp-section-head">
              <div>
                <h2 className="vp-heading" style={{ fontSize: 24 }}>Received</h2>
                <p>{pendingReceived.length ? `${pendingReceived.length} people want to connect` : "You're all caught up"}</p>
              </div>
              <span className="vp-pill">{pendingReceived.length} pending</span>
            </div>
            {pendingReceived.length > 0 ? (
              <div className="vp-list-grid">
                {pendingReceived.map(req => (
                  <article key={req.email} className="vp-network-person">
                    <div className="vp-avatar-sm" style={{ background: isExternalParticipant(req) ? '#d4a7f7' : '#dce5ff' }}>
                      {req.profile_pic ? <img src={req.profile_pic} alt="" /> : getInitials(req.name)}
                    </div>
                    <div className="vp-network-person-main">
                      <h3>{req.name}</h3>
                      <div className="vp-person-meta">
                        {isExternalParticipant(req) ? `${req.college || 'External'} · ${req.department || ''}` : `${req.department} · ${req.year}`}
                      </div>
                      {req.bio && <p style={{ fontSize: 12, color: 'var(--vp-muted)', margin: '10px 0 0', lineHeight: 1.5, fontStyle: 'italic' }}>"{req.bio}"</p>}
                      <div className="vp-network-person-actions">
                        <button className="vp-btn vp-btn-dark vp-btn-small" onClick={() => handleAcceptConnect(req)}>Accept</button>
                        <button className="vp-btn vp-btn-outline vp-btn-small" onClick={() => handleRejectConnect(req)}>Not now</button>
                        <button className="vp-btn vp-btn-soft vp-btn-small" onClick={() => openProfile(req)}>View profile</button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="vp-empty-state" style={{ gridColumn: '1 / -1' }}>
                <div className="vp-empty-icon">✓</div>
                <h3 className="vp-heading">No pending requests</h3>
                <p>When someone wants to connect, you'll see them here.</p>
                <button className="vp-btn vp-btn-dark" onClick={() => setActiveCategory('all')}>Discover people</button>
              </div>
            )}

            {/* Sent */}
            <div className="vp-section-head">
              <div>
                <h2 className="vp-heading" style={{ fontSize: 24 }}>Sent</h2>
                <p>Invitations you've already sent</p>
              </div>
            </div>
            {pendingSent.length > 0 ? (
              <div className="vp-list-grid">
                {pendingSent.map(p => (
                  <article key={p.email} className="vp-network-person">
                    <div className="vp-avatar-sm">
                      {p.profile_pic ? <img src={p.profile_pic} alt="" /> : getInitials(p.name)}
                    </div>
                    <div className="vp-network-person-main">
                      <h3>{p.name}</h3>
                      <div className="vp-person-meta">
                        {isExternalParticipant(p) ? `${p.college || 'External'} · ${p.department || ''}` : `${p.department} · ${p.year}`}
                      </div>
                      <div className="vp-network-person-actions">
                        <span className="vp-pill">Request sent</span>
                        <button className="vp-btn vp-btn-soft vp-btn-small" onClick={() => openProfile(p)}>View profile</button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="vp-empty-state">
                <p>You haven't sent any requests yet.</p>
                <button className="vp-btn vp-btn-outline" onClick={() => setActiveCategory('all')}>Find someone to connect with</button>
              </div>
            )}
          </div>
        )}

        {/* ====== MY NETWORK VIEW ====== */}
        {isNetworkView && (
          <div className="vp-view">
            <div className="vp-section-intro">
              <div>
                <div className="vp-eyebrow">Your people, in one place</div>
                <h1 className="vp-heading" style={{ fontSize: 'clamp(32px,4.5vw,48px)' }}>My network.</h1>
                <p>Connections you've made through VJLINK events. Pick up a conversation, revisit a profile, or find someone new to meet.</p>
              </div>
              <button className="vp-btn vp-btn-dark" onClick={() => setActiveCategory('all')}>Find more people ↗</button>
            </div>

            <div className="vp-discover-toolbar">
              <label className="vp-search">
                <span><Search size={16} style={{ color: 'var(--vp-muted)' }} /></span>
                <input 
                  placeholder="Search your connections…" 
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </label>
            </div>

            {networkSearched.length > 0 ? (
              <div className="vp-list-grid">
                {networkSearched.map(conn => {
                  const phoneNum = String(conn.contact || conn.phone || '').replace(/\D/g, '');
                  return (
                    <article key={conn.email} className="vp-network-person">
                      <div className="vp-avatar-sm" style={{ background: isExternalParticipant(conn) ? '#d4a7f7' : '#dce5ff' }}>
                        {conn.profile_pic ? <img src={conn.profile_pic} alt="" /> : getInitials(conn.name)}
                      </div>
                      <div className="vp-network-person-main">
                        <h3>{conn.name}</h3>
                        <div className="vp-person-meta">
                          {isExternalParticipant(conn) ? `${conn.college || 'External'} · ${conn.department || ''}` : `${conn.department} · ${conn.year}`}
                        </div>
                        <div className="vp-tag-row" style={{ marginTop: 9 }}>
                          {safeArray(conn.skills).slice(0, 3).map((s, i) => (
                            <span key={i} className="vp-skill-tag">{s}</span>
                          ))}
                        </div>
                        {phoneNum && (
                          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                              <Phone size={12} color="var(--vp-green)" /> {conn.contact || conn.phone}
                            </span>
                            <a 
                              href={`https://wa.me/91${phoneNum}`} 
                              target="_blank" 
                              rel="noreferrer"
                              style={{ color: 'var(--vp-green)', fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}
                            >
                              <MessageSquare size={12} /> WhatsApp
                            </a>
                          </div>
                        )}
                        <div className="vp-network-person-actions">
                          <button className="vp-btn vp-btn-dark vp-btn-small" onClick={() => openProfile(conn)}>View profile ↗</button>
                          <button className="vp-btn vp-btn-outline vp-btn-small" onClick={() => showToast('Messaging can be connected to your approved communication workflow.')}>
                            Message
                          </button>
                          <button className="vp-btn vp-btn-soft vp-btn-small" style={{ color: '#a53b31' }} onClick={() => handleRemoveConnect(conn)}>Remove</button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="vp-empty-state">
                <div className="vp-empty-icon">♧</div>
                <h3 className="vp-heading">{searchQuery ? 'No people found' : 'Your network starts here'}</h3>
                <p>{searchQuery ? 'Try another name, college or skill.' : 'Connect with people you want to learn from or build with.'}</p>
                <button className="vp-btn vp-btn-dark" onClick={() => setActiveCategory('all')}>Discover people ↗</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ====== MOBILE BOTTOM NAV ====== */}
      <nav className="vp-bottom-nav" aria-label="Mobile navigation">
        <button onClick={() => navigate(`/events/${slug}`)} className={isDiscoverView ? '' : ''}>
          <span className="vp-nav-symbol">⌂</span>Event
        </button>
        <button className={isDiscoverView ? 'active' : ''} onClick={() => setActiveCategory('all')}>
          <span className="vp-nav-symbol">◎</span>Discover
        </button>
        <button className={isRequestsView ? 'active' : ''} onClick={() => setActiveCategory('requests')}>
          <span className="vp-nav-symbol">↗</span>Requests {pendingReceived.length > 0 && <span>{pendingReceived.length}</span>}
        </button>
        <button className={isNetworkView ? 'active' : ''} onClick={() => setActiveCategory('connections')}>
          <span className="vp-nav-symbol">♧</span>My network
        </button>
      </nav>

      {/* ====== TOAST ====== */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div 
            className="vp-toast show"
            initial={{ y: 20, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            exit={{ y: 20, opacity: 0 }}
            style={{ pointerEvents: 'auto' }}
          >
            <Check size={16} color="var(--vp-green)" />
            <span>{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ====== PROFILE ONBOARDING MODALS ====== */}
      {showOnboarding && isExternal && (
        <ExternalProfileOnboardingModal
          isOpen={showOnboarding}
          eventSlug={slug}
          eventName={eventData?.name || slug}
          user={user}
          onComplete={(updatedUser) => {
            setShowOnboarding(false);
            if (updatedUser) setUser(updatedUser);
            const activeEmail = updatedUser?.email || user?.email || user?.login_email;
            if (activeEmail) {
              localStorage.setItem(`vjlink_profile_completed_${slug}_${activeEmail}`, 'true');
            }
            refreshProfileAndData(updatedUser || user);
          }}
          onClose={() => setShowOnboarding(false)}
        />
      )}

      {showOnboarding && !isExternal && (
        <EventProfileOnboardingModal 
          eventSlug={slug} 
          eventName={eventData?.name || slug} 
          user={user} 
          activities={eventData?.activities || []} 
          onComplete={(updatedUser) => {
            setShowOnboarding(false);
            if (updatedUser) setUser(updatedUser);
            const activeEmail = updatedUser?.email || user?.email;
            if (activeEmail) {
              localStorage.setItem(`vjlink_profile_completed_${slug}_${activeEmail}`, 'true');
            }
            refreshProfileAndData(updatedUser || user);
          }}
          onClose={() => setShowOnboarding(false)} 
        />
      )}

      {/* ====== PROFILE DRAWER ====== */}
      <AnimatePresence>
        {activeProfile && (
          <EventParticipantProfileModal 
            participant={activeProfile} 
            matchData={activeProfile.matchData} 
            eventSlug={slug} 
            currentUserEmail={user?.email || user?.login_email} 
            onClose={closeProfile}
            onConnect={handleConnect}
            onAccept={handleAcceptConnect}
            onReject={handleRejectConnect}
            onRemove={handleRemoveConnect}
            connectionStatus={activeProfile.connectionStatus}
            onPrev={() => navigateDrawer(-1)}
            onNext={() => navigateDrawer(1)}
            hasPrev={selectedProfileIndex > 0}
            hasNext={selectedProfileIndex < currentDisplayedList.length - 1}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
